import { CloudOff } from 'lucide-react'
import { useLocale } from '../../i18n/context'

interface Props {
  /** The desktop list row runs this at 9.5px inside a tighter row; the card
   * surfaces share one larger size. Layout only — the copy is identical. */
  compact?: boolean
}

/**
 * The change is still here, and it has not been saved (ADR-072 Decision 4).
 *
 * This is the ONLY mark a card edit ever carries, and it appears only after a
 * write has failed. There is deliberately no counterpart for a write that is
 * merely unanswered: this surface commits every time the caret leaves a field,
 * so an in-progress mark would be the resting state of anyone working on a
 * card, and a signal that is always on says nothing while still spending
 * attention on a screen whose whole job is reading and typing text
 * (Decision 3).
 *
 * It says the change is still there, because that is the part the person cannot
 * see for themselves — their text is on screen exactly as they left it, which
 * is precisely what makes "this did not save" surprising. The transient report
 * that accompanies the failure says what to do about it; this is what remains
 * once that report has gone, for as long as the change is unsaved.
 */
export default function UnsavedMark({ compact = false }: Props) {
  const { tr } = useLocale()
  const cb = tr.cardBubble

  return (
    <span
      // A live region: the mark can appear while the reader's attention is in a
      // text field somewhere else on the card, which is exactly the case where
      // a purely visual change is missed.
      role="status"
      title={cb.unsavedExplanation}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: compact ? 4 : 5,
        flexShrink: 0,
        background: 'var(--danger-soft)', color: 'var(--danger)',
        border: '1px solid var(--danger)',
        borderRadius: 'var(--radius-pill)',
        padding: compact ? '0px 6px' : '2px 8px',
        fontFamily: 'var(--font-ui)', fontWeight: 600,
        fontSize: compact ? 9.5 : 11, whiteSpace: 'nowrap',
      }}
    >
      <CloudOff size={compact ? 10 : 12} strokeWidth={2} />
      {cb.unsavedLabel}
    </span>
  )
}
